import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { CircleOfFifths } from "@/components/CircleOfFifths";
import { IntervalExplorer } from "@/components/mt/IntervalExplorer";
import { NextUp } from "@/components/Callouts";
import { circleIndex } from "@/theory/notes";

// Enharmonic-tolerant: idx("Bb") and idx("Bb") find the same seat.
const idx = circleIndex;

export default function MusicTheory13() {
  const { lesson, course, accent } = findLesson("music-theory", "13");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT13-Intervals-on-Circle-Animated.mp4"
        caption="Every interval is a chord drawn across the circle of fifths"
        hint="≈ 40 sec · watch first"
      />
      <Chapter number="13" eyebrow="Music Theory · Chapter Thirteen" title="The circle is also an interval map">
        <Prose>
          <p>
            We've used the circle of fifths to organise key signatures and to remember
            the spelling of scales. There's a third use that, once you see it, makes
            interval recognition almost embarrassingly fast: the very <em>geometry</em>
            of the circle is an interval map. Distance on the circle corresponds, in a
            very direct way, to the emotional flavour of the interval between two notes.
            Things that sit close together sound stable and consonant. Things that sit
            far apart sound restless and dissonant.
          </p>
          <p>
            For this chapter we'll pick a less common root than usual. C is fine, but
            E♭ keeps the demonstration honest — every interval has to be reasoned out.
            Once the method works for E♭, you can transpose it to any root in seconds.
          </p>
        </Prose>
        <MTQuote>
          Remembering them through the circle of fifths may actually be the way to go to
          find the true nature of how that bond occurs between the notes.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Setting up the root" title="Double-circle E♭ and meet its neighbours">
        <Prose>
          <p>
            Here's the working convention: take any chosen root and <em>double-circle</em>
            it on your hand-drawn circle of fifths. That circle inside a circle marks
            this note as the tonal centre — your <em>sa</em>, your home note, the place
            everything else is measured from. We'll use E♭ for this lesson.
          </p>
          <p>
            E♭'s clockwise neighbour on the circle is <strong>B♭</strong>. That neighbour
            is always a perfect fifth from the root. E♭'s counter-clockwise neighbour is
            <strong> A♭</strong>. That neighbour is always a perfect fourth from the
            root. So just by glancing at the two seats next to E♭, you've already named
            three of its scale tones: the root itself (the 1), its fourth (the 4), and
            its fifth (the 5).
          </p>
        </Prose>

        <div className="mx-auto flex max-w-3xl justify-center py-4">
          <CircleOfFifths
            size={360}
            rootIndex={idx("Eb")}
            highlights={[
              { index: idx("Ab"), color: "#10b981", label: "4" },
              { index: idx("Eb"), color: "#f59e0b", label: "1" },
              { index: idx("Bb"), color: "#10b981", label: "5" },
              { index: idx("F"),  color: "#3b82f6", label: "2" },
              { index: idx("C"),  color: "#3b82f6", label: "6m" },
              { index: idx("G"),  color: "#3b82f6", label: "3" },
              { index: idx("D"),  color: "#3b82f6", label: "7" },
              { index: idx("A"),  color: "#ef4444", label: "TT" },
            ]}
          />
        </div>

        <MTQuote>
          E♭ is there. Up one, clock one, you get its fifth, B♭. Counter clock one, you get
          its fourth, namely A♭. So I think this is really cool. It allows you to just
          remember three notes.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Build the major scale by arcing" title="Continue clockwise to fill in the rest">
        <Prose>
          <p>
            E♭ has its 4 (A♭), 1 (E♭), 5 (B♭). To fill out the major scale we just
            <strong> continue arcing clockwise</strong> from B♭. The next four
            clockwise positions hand us the remaining four scale tones in this order:
            <strong> F, C, G, D</strong>.
          </p>
        </Prose>

        <Steps>
          <Step><strong>Two o'clock from the root → major 2nd.</strong> E♭'s major 2 is the second clockwise step beyond B♭ — namely <strong>F</strong>.</Step>
          <Step><strong>Three o'clock from the root → major 6th (and relative minor).</strong> The next step is <strong>C</strong>. C is E♭'s major 6, and C minor is E♭ major's relative minor.</Step>
          <Step><strong>Four o'clock from the root → major 3rd.</strong> Continue and you land on <strong>G</strong>. G is E♭'s major 3.</Step>
          <Step><strong>Five o'clock from the root → major 7th.</strong> The farthest of the seven scale tones is <strong>D</strong>. D is E♭'s major 7 — the brightest leading tone.</Step>
        </Steps>

        <Prose>
          <p>
            That's the entire E♭ major scale: A♭, E♭, B♭, F, C, G, D — read straight off
            the circle, no recipe-counting required. Reorder them by pitch and you have
            E♭ F G A♭ B♭ C D, which is exactly the seven notes of E♭ major.
          </p>
        </Prose>

        <TheoryBox label="The phone-number trick: 4 1 5 2 6 3 7">
          From any root, the major scale tones are already lined up in a fixed pattern
          on the circle of fifths. Walking out from the root, the order of degrees you
          meet is <strong>4 — 1 — 5 — 2 — 6 — 3 — 7</strong>. Memorise that sequence
          like a phone number; you can build any major scale from any tonic by just
          reading neighbours off the circle.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The geometry of consonance" title="Closer = peaceful, farther = dissonant">
        <Prose>
          <p>
            Now the magic. The circle isn't just a memory aid — it actually predicts how
            an interval will <em>feel</em>. The closer a note sits to the root on the
            circle, the more consonant and resolved its interval sounds. The farther
            away, the more tension. Geometry maps onto emotion.
          </p>
          <p>
            Walk outward from E♭ and listen to the colours emerge:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>One step away (P4, P5)</strong> — perfect, stable, neutral. A♭ and B♭ over E♭ feel like home.</li>
            <li><strong>Two steps (M2, m7)</strong> — anticipation; the scale is in motion. F over E♭ wants to resolve.</li>
            <li><strong>Three steps (M6, m3)</strong> — emotional weight. C over E♭ glows; the relative minor begins.</li>
            <li><strong>Four steps (M3, m6)</strong> — the bright/dark third that defines major and minor. G is E♭'s major 3rd.</li>
            <li><strong>Five steps (M7, m2)</strong> — tension just before resolution. D over E♭ is on the edge of returning home.</li>
            <li><strong>Six steps — the diameter (tritone)</strong> — the maximum possible distance, the harshest dissonance. A over E♭.</li>
          </ul>
        </Prose>

        <TheoryBox label="The diameter is the dissonance">
          The single greatest distance between any two points on a circle is the
          diameter. On the circle of fifths, a root's diametric opposite is its
          <strong> tritone</strong> — the most dissonant interval of all. E♭ to A is the
          diameter; that's why it sounds like the door to a horror film. The geometry
          isn't a metaphor; it's the answer.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The dark side" title="Walk counter-clockwise for the minor intervals">
        <Prose>
          <p>
            Walking clockwise from the root gives us the bright intervals — major and
            perfect. Walking counter-clockwise gives us the darker family. From E♭:
          </p>
        </Prose>

        <Steps>
          <Step><strong>Two steps counter-clockwise → minor 7th.</strong> E♭ to D♭ is a minor 7 — the dominant-seventh flavour.</Step>
          <Step><strong>Three steps counter-clockwise → minor 3rd.</strong> E♭ to G♭ (= F♯) is a minor 3 — the saddest interval inside any minor chord.</Step>
          <Step><strong>Four steps counter-clockwise → minor 6th.</strong> E♭ to B (= C♭) is a minor 6 — yearning, romantic, often unresolved.</Step>
          <Step><strong>Five steps counter-clockwise → minor 2nd.</strong> E♭ to E (= F♭) is a minor 2 — the half-step, just as dissonant as the major 7 but in the opposite direction.</Step>
        </Steps>

        <Prose>
          <p>
            And the diameter — the tritone — is reached after exactly six steps in
            <em> either</em> direction. Clockwise or counter-clockwise, you arrive at the
            same dissonant opposite. That's the elegant proof that the tritone really is
            the maximum distance: it's the only point on the circle that doesn't favour
            one walk over the other.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Hands-on" title="Hear every interval from any root">
        <Prose>
          <p>
            The interval explorer below lets you pick a root and click through every
            interval. Notice how the moods cluster: green (resolution) lives near the
            root, amber (anticipation) two steps out, rose (tension) on the far edges —
            including the tritone at the diameter — and the purple "mystery" on the two
            6ths, whose role depends entirely on the chord beneath them.
          </p>
        </Prose>

        <IntervalExplorer root="C" />

        <Glossary>
          <Term name="Tonal centre (sa)" def="The home note of a passage. Everything else is measured from here. Double-circle it on your hand-drawn circle of fifths." />
          <Term name="Diameter" def="The line straight across a circle. On the circle of fifths it connects each root to its tritone — the maximum dissonance." />
          <Term name="Phone-number pattern" def="4 1 5 2 6 3 7 — the order in which scale degrees appear on the circle as you arc out from the tonic." />
          <Term name="Brightness gradient" def="Clockwise = bright (major, perfect, raised). Counter-clockwise = dark (minor, flattened)." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="Practice" title="Redraw the circle, every day">
        <Prose>
          <p>
            The circle pays you back in proportion to how often you draw it. Don't rely
            on printed posters; draw it from memory, double-circle a different root each
            time, and trace the 4-1-5-2-6-3-7 path with your finger. After a week or
            two the geometry stops being a chart and starts being a way of seeing the
            pitches. That perception is what lets musicians improvise over chords they
            haven't seen before — they're not guessing, they're reading distance.
          </p>
        </Prose>

        <MTQuote>
          The more you keep writing down something like the circle of fifths, the more
          obvious it gets into your brain. So I would encourage you to write it.
        </MTQuote>
        <NoteImage
          src="/notes/music-theory/finals/13a-intervals-of-Cb-major-on-circle.png"
          caption="Jason's circle-of-fifths interval lookup applied to C♭ major. Walk the circle clockwise from the root: P4 → 1 → P5 → M2 → M6 → M3 → M7. The famous '4-1-5-2-6-3-7' sequence — instant interval recall in any key, no tone-counting required."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-14"
        label="Chapter 14 · Circle of Fifths for Triads"
        why="The same neighbour-and-arc technique that built scales also builds chords. Three majors clustered around the root, three minors next to them, and a single diminished chord at the far edge."
      />
    </LessonShell>
  );
}
